import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { BetterDocInsuranceCountrySelect } from "@/modules/admin/better-doc/components/better-doc-insurance-country-select"
import {
  BetterDocTestCaseResultRow,
  BetterDocTestCaseResultTable,
} from "@/modules/admin/better-doc/components/better-doc-test-case-result-table"
import { DEFAULT_INSURANCE_COUNTRY } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { toTestIdSegment } from "@/modules/admin/better-doc/lib/to-test-id-segment"
import { useSendBetterDocTestCaseMutation } from "@/modules/admin/better-doc/services/useSendBetterDocTestCaseMutation"
import {
  Country,
  de_BetterDocTestCaseScenario,
  de_BetterDocTestCaseScenarioValues,
} from "@/types/__generated__/union-types"

export const BetterDocTestCaseSender = () => {
  const { t } = useTranslation()
  const [sendTestCase, isSending] = useSendBetterDocTestCaseMutation()
  const [insuranceCountry, setInsuranceCountry] = useState<Country>(DEFAULT_INSURANCE_COUNTRY)
  const [sendingScenario, setSendingScenario] = useState<de_BetterDocTestCaseScenario | null>(null)
  const [resultRows, setResultRows] = useState<ReadonlyArray<BetterDocTestCaseResultRow>>([])

  const handleSend = (scenario: de_BetterDocTestCaseScenario) => {
    setSendingScenario(scenario)
    sendTestCase({
      variables: { input: { scenario, insuranceCountry } },
      onCompleted: (response) => {
        const { testCaseResult } = response.de_sendBetterDocTestCase
        if (testCaseResult !== null) {
          setResultRows((currentRows) => [
            { resultNumber: currentRows.length + 1, scenario, result: testCaseResult },
            ...currentRows,
          ])
        }
        setSendingScenario(null)
      },
      onError: () => setSendingScenario(null),
    })
  }

  return (
    <Card data-testid="better-doc-admin-test-case-sender">
      <CardHeader>
        <CardHeaderTitle>{t("admin.betterDoc.testCaseSender.title")}</CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <p className="text-on-surface-subdued text-sm">{t("admin.betterDoc.testCaseSender.description")}</p>
        <div className="flex flex-col gap-2">
          <BetterDocInsuranceCountrySelect
            insuranceCountry={insuranceCountry}
            onInsuranceCountryChange={setInsuranceCountry}
            testId="better-doc-admin-test-case-insurance-country"
          />
          <p className="text-on-surface text-sm" data-testid="better-doc-admin-test-case-seed-patient">
            {t(`admin.betterDoc.testCaseSender.seedPatient.${insuranceCountry}`)}
          </p>
          <p className="text-on-surface-subdued text-xs">{t("admin.betterDoc.testCaseSender.seedPatientNote")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {de_BetterDocTestCaseScenarioValues.map((scenario) => (
            <Button
              key={scenario}
              type="button"
              variant="secondary"
              isLoading={sendingScenario === scenario}
              disabled={isSending}
              onClick={() => handleSend(scenario)}
              data-testid={`better-doc-admin-send-test-case-${toTestIdSegment(scenario)}-button`}>
              {getBetterDocEnumLabel(t, "testCaseScenario", scenario)}
            </Button>
          ))}
        </div>
        {resultRows.length === 0 ? null : (
          <div className="flex flex-col gap-2" data-testid="better-doc-admin-test-case-results">
            <h3 className="text-on-surface text-base font-medium">{t("admin.betterDoc.testCaseSender.results")}</h3>
            <BetterDocTestCaseResultTable rows={resultRows} />
            <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-test-case-progress-hint">
              {t("admin.betterDoc.testCaseSender.progressHint")}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
